import { useEffect, useRef } from 'react';

/**
 * Custom hook for implementing infinite scroll with Intersection Observer
 * Works with React Query's useInfiniteQuery
 *
 * @param hasNextPage - Whether there are more pages to load
 * @param isFetchingNextPage - Whether currently fetching next page
 * @param fetchNextPage - Function to fetch the next page
 * @param threshold - Visibility threshold (0-1), defaults to 0.1
 * @param rootMargin - Margin around root element for early triggering, defaults to '100px'
 * @returns ref - Ref to attach to the load-more trigger element
 */
export function useInfiniteScroll(
  hasNextPage: boolean | undefined,
  isFetchingNextPage: boolean,
  fetchNextPage: () => Promise<unknown>,
  threshold: number = 0.1,
  rootMargin: string = '100px'
) {
  const loadMoreRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const target = entries[0];
        if (target.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage().catch((error) => {
            console.error('fetchNextPage error:', error);
          });
        }
      },
      {
        threshold,
        rootMargin,
      }
    );

    const currentRef = loadMoreRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, threshold, rootMargin]);

  return loadMoreRef;
}
